<script lang="ts" setup>
import FloatLabel from '@shared/form-v2/FloatLabel/FloatLabel.vue';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import EditButton from '@relatie-lib/EditButton.vue';
import UboStatus from '@relatie-lib/ubo/UboStatus.vue';
import { type CrmUbosUboDto } from '@services/crm-public/orval-api/model/crmUbosUboDto';
import type { SUREbusinessCommonDomainValueObjectsUboAdres } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainValueObjectsUboAdres';
import { useUboCreate, useUboDelete, useUboUpdate } from '@services/crm-public/orval-api/ubo/ubo';
import { getAdres } from '@services/crm-public/postcodeService';
import { useLandenGetLanden } from '@services/overig/orval-api/landen-api/landen-api';
import { VButton } from '@shared/component/VButton';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import { ModalType } from '@shared/component/types';
import AdresField from '@shared/form/AdresField/AdresField.vue';
import { useAdresFieldProps } from '@shared/form/AdresField/useAdresFieldProps';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import StringField from '@shared/form/StringField/StringField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import VSelect from '@shared/form/VSelect/VSelect.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { addDays, toDateonly } from '@tools/dates';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, inject, ref, unref, useId, watch } from 'vue';
import { toonUboCheckResultatenInjectionKey, uboContextInjectionKey } from './keys';
import { type Ubo, type UboAdres, type UboMetCheckResultaat } from './types';
import { uboSchema } from './uboSchema';

const { autoSave, index, kanVerwijderen, relatieId, shouldConfirmRemoval } = defineProps<{
  index: number;
  relatieId: string;
  kanBewerken?: boolean;
  kanVerwijderen?: boolean;
  autoSave?: boolean;
  shouldConfirmRemoval?: boolean;
}>();

const emit = defineEmits<{ removed: [string]; changed: [string] }>();

const context = inject(uboContextInjectionKey);

const notification = useNotification();

const ubo = defineModel<Ubo | UboMetCheckResultaat>();
const showConfirmation = ref(false);

const {
  values,
  defineField,
  resetForm,
  handleSubmit,
  isSubmitting,
  meta,
  validate,
  errors: formErrors,
} = useForm<Ubo>({
  validationSchema: toTypedSchema(uboSchema),
  initialValues: {},
});

const uboCheckNotificationText = `We gaan de opgegeven UBO even voor je checken, dit kan een paar seconden duren.\nVernieuw de pagina om het resultaat van de check te zien.`;
const { mutate: updateUbo, error: updateUboError } = useUboUpdate({
  mutation: {
    onSuccess: () => {
      if (!autoSave) {
        notification.success(`UBO #${index} opgeslagen.<br /><br />${uboCheckNotificationText}`);
      }
      emit('changed', ubo.value?.id ?? '');
    },
  },
});
useWatchErrorHandler(updateUboError);

const { mutate: createUbo, error: createUboError } = useUboCreate({
  mutation: {
    onSuccess: (id: string) => {
      if (!autoSave) {
        notification.success(`UBO #${index} aangemaakt.<br /><br />${uboCheckNotificationText}`);
      }
      emit('changed', id);
    },
  },
});
useWatchErrorHandler(createUboError);

const onSubmit = handleSubmit(async () => {
  const result = await validate();
  if (!result.valid) {
    return false;
  }
  if (ubo.value?.id) {
    updateUbo({ uboId: ubo.value.id, data: mapToDto(values) });
  } else {
    createUbo({ relatieId, data: mapToDto(values) });
  }
  return true;
});

defineExpose({
  onSubmit,
  validate,
});

function mapNederlandsAdres(adres: UboAdres): SUREbusinessCommonDomainValueObjectsUboAdres {
  return {
    postcode: adres?.postcode ?? '',
    huisnummer: adres?.huisnummer ?? null,
    huisnummertoevoeging: adres?.huisnummertoevoeging ?? '',
    straat: adres?.straat ?? '',
    plaats: adres?.plaats ?? '',
    land: adres?.land ?? '',
  };
}

function mapBuitenlandsAdres(adres: UboAdres): SUREbusinessCommonDomainValueObjectsUboAdres {
  return {
    postcode: adres?.postcode ?? '',
    adres: adres?.adres ?? '',
    plaats: adres?.plaats ?? '',
    land: adres?.land ?? '',
  };
}

function mapToDto(u: Ubo): CrmUbosUboDto {
  const adres = u.adres.isNederlands ? mapNederlandsAdres(u.adres) : mapBuitenlandsAdres(u.adres);
  return {
    ...u,
    voorletters: u.voorletters ?? '',
    geboortedatum: u.geboortedatum ? toDateonly(u.geboortedatum) : '',
    adres,
    uboContext: unref(context?.value) ?? 'Algemeen',
  };
}

watch(
  ubo,
  (_ubo) => {
    resetForm({
      values: {
        voorletters: _ubo?.voorletters,
        achternaam: _ubo?.achternaam,
        geboortedatum: _ubo?.geboortedatum,
        adres: {
          land: _ubo?.adres.land || 'Nederland',
          postcode: _ubo?.adres.postcode || '',
          plaats: _ubo?.adres.plaats || '',
          straat: _ubo?.adres.straat || '',
          huisnummer: _ubo?.adres.huisnummer ?? null,
          huisnummertoevoeging: _ubo?.adres.huisnummertoevoeging || '',
          adres: _ubo?.adres.adres || '',
          isNederlands: _ubo?.adres.isNederlands ?? true,
        },
        id: _ubo?.id,
      },
    });
  },
  { deep: true, immediate: true },
);

const [voorletters] = defineField('voorletters');
const [achternaam] = defineField('achternaam');
const [geboortedatum] = defineField('geboortedatum');
const [adres] = defineField('adres.adres');
const [plaats] = defineField('adres.plaats');
const [land] = defineField('adres.land');
const [postcode] = defineField('adres.postcode');
const [isNederlands] = defineField('adres.isNederlands');

const maxGeboortedatum = addDays(new Date(), -1);

watch(
  land,
  () => {
    if (!land.value) return;

    isNederlands.value = land.value === 'Nederland';
  },
  { immediate: true },
);

const { errors: adresErrors, adresFieldPaths } = useAdresFieldProps();

const { data: landen, error: landenError } = useLandenGetLanden({
  query: {
    select: (e) => e.result?.map((r) => r.naam).sort((a, b) => a.localeCompare(b)),
    staleTime: 60 * 1000,
  },
});
useWatchErrorHandler(landenError);

const { mutateAsync: deleteUbo, error: deleteUboError } = useUboDelete();
useWatchErrorHandler(deleteUboError);

async function remove() {
  showConfirmation.value = false;
  if (ubo.value?.id) {
    await deleteUbo({ uboId: ubo.value.id });
  }
  emit('removed', ubo.value?.id ?? '');
}

const id = useId();
const readonly = computed(() => (autoSave ? false : !ubo.value?.inEditMode));

const toonCheckResultaten = inject(toonUboCheckResultatenInjectionKey, { value: ref(false) });

const isFormValid = computed(() => meta.value.valid && !!ubo.value?.id);
const isHiddenForAdvisor = computed(
  () => readonly.value && ubo.value && !('resultaat' in ubo.value),
);

const valid = computed(() => (isHiddenForAdvisor.value ? undefined : isFormValid.value));
const isExistingUBO = computed(() => !!ubo.value?.id);
</script>

<template>
  <VContentBox :title="`UBO #${index}`" :valid>
    <template #header-start v-if="unref(toonCheckResultaten.value)">
      <UboStatus v-if="ubo && 'resultaat' in ubo" v-model="ubo" />
    </template>
    <template #header-end>
      <div class="flex">
        <EditButton
          v-if="kanVerwijderen && ubo?.inEditMode"
          icon="far fa-trash"
          title="Verwijder UBO"
          @click="shouldConfirmRemoval && isExistingUBO ? (showConfirmation = true) : remove()"
        />
        <EditButton
          title="UBO"
          v-if="ubo && ubo.id && kanBewerken && !autoSave"
          v-model="ubo.inEditMode"
        />
      </div>
    </template>
    <div class="flex flex-col gap-2">
      <div class="flex flex-col gap-4">
        <FieldRowV2 label="Naam" :errors="[formErrors.voorletters, formErrors.achternaam]">
          <FloatLabel class="grow-0 basis-full md:basis-28" v-if="!readonly">
            <StringField
              focus-on-mount
              :has-error="!!formErrors.voorletters"
              :id="`voorletters-${id}`"
              v-model:value="voorletters"
            />
            <label :for="`voorletters-${id}`">Voorletters</label>
          </FloatLabel>
          <FloatLabel v-if="!readonly">
            <StringField
              :id="`achternaam-${id}`"
              v-model:value="achternaam"
              :has-error="!!formErrors.achternaam"
            />
            <label :for="`achternaam-${id}`">Achternaam</label>
          </FloatLabel>
          <div v-if="readonly">
            {{ [voorletters, achternaam].map((val) => val || '-').join(' ') }}
          </div>
        </FieldRowV2>

        <FieldRowV2
          label="Geboortedatum"
          :label-for="`geboortedatum-${id}`"
          :errors="[formErrors.geboortedatum]"
        >
          <VDatePicker
            :readonly
            :id="`geboortedatum-${id}`"
            v-model="geboortedatum"
            :max-date="maxGeboortedatum"
            :clearable="false"
            full-width
            @blur="validate"
            :invalid="!!formErrors.geboortedatum"
          />
        </FieldRowV2>

        <FieldRowV2
          label="Adres"
          :errors="
            isNederlands ? adresErrors : [formErrors['adres.adres'], formErrors['adres.plaats']]
          "
        >
          <AdresField :readonly :lookup="getAdres" :adres-field-paths v-if="isNederlands" />

          <template v-if="!isNederlands && !readonly">
            <FloatLabel class="basis-24 md:basis-12">
              <StringField :readonly :id="`postcode-buitenland-${id}`" v-model:value="postcode" />
              <label :for="`postcode-buitenland-${id}`">Postcode</label>
            </FloatLabel>
            <FloatLabel>
              <StringField
                :readonly
                :id="`adres-${id}`"
                @blur="validate"
                v-model:value="adres"
                :has-error="!!formErrors['adres.adres']"
              />
              <label :for="`adres-${id}`">Adres</label>
            </FloatLabel>
            <FloatLabel class="basis-full">
              <StringField
                :readonly
                :id="`plaats-${id}`"
                @blur="validate"
                v-model:value="plaats"
                :has-error="!!formErrors['adres.plaats']"
              />
              <label :for="`plaats-${id}`">Plaats</label>
            </FloatLabel>
          </template>
          <div v-if="readonly && !isNederlands">
            {{
              [postcode, adres, plaats].every((val) => !val)
                ? '-'
                : [postcode, adres, plaats].filter(Boolean).join(', ')
            }}
          </div>

          <FloatLabel v-if="!readonly" class="basis-full">
            <VSelect :id="`land-${id}`" :options="landen" searchable v-model="land" />
            <label :for="`land-${id}`">Land</label>
          </FloatLabel>
          <div class="basis-full" v-if="readonly">{{ land || '-' }}</div>
        </FieldRowV2>
      </div>
      <div v-if="ubo?.inEditMode && !autoSave" class="flex w-full justify-end">
        <VButton :loading="isSubmitting" @click="onSubmit()">Wijzigingen opslaan</VButton>
      </div>
    </div>
  </VContentBox>
  <VModal
    confirm-button-text="UBO verwijderen"
    :is-active="showConfirmation"
    :type="ModalType.Danger"
    @confirm-action="remove()"
    @close-modal="() => (showConfirmation = false)"
  >
    Weet je zeker dat je deze UBO wilt verwijderen?
  </VModal>
</template>
